import React from "react";
import type { AutocompleteInputChangeReason } from "@mui/material";
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close, Tune } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  theme as baseTheme,
  CALIBRATE_COLORS,
  CALIBRATE_FONT_FAMILY_ACCENT,
  CALIBRATE_RADIUS_MD,
} from "components/hoc/Theme";
import { SHELL_MAIN_CHROME_PADDING_PX } from "config";
import FileSearch from "components/fileStorage/FileSearch";
import PaginationControls from "components/fileStorage/PaginationControls";
import UnsavedPlatformDraftsDialog from "components/userManagement/UnsavedPlatformDraftsDialog";
import UserEditModal from "components/userManagement/UserEditModal";
import BackendUserEditModal from "components/userManagement/BackendUserEditModal";
import ClientsTable from "components/userManagement/ClientsTable";
import ClientEditModal from "components/userManagement/ClientEditModal";
import {
  CREATE_MODE_DRAFT_KEY,
  type UserManagementDrafts,
  PLATFORM_SELECTED_USER_STORAGE_KEY,
  clearPersistedPlatformUserSelection,
  clearPlatformUserDrafts,
  hasAnyPlatformUserDrafts,
  loadPlatformUserDrafts,
  mergeDraftClientNamesToSession,
  savePlatformUserDrafts,
  summarizeDraftsForDialog,
} from "userManagement/platformDraftsStorage";
import UserManagementTable from "components/userManagement/UserManagementTable";
import BackendUserTable from "components/userManagement/BackendUserTable";
import {
  GetAdminUsersParams,
  useGetAdminUsersQuery,
  useGetBackendUsersQuery,
  useGetClientsQuery,
} from "features/adminUsers/adminUsers";
import { useClientsAutocompleteInfinite } from "features/adminUsers/useClientsAutocompleteInfinite";
import { useAppDispatch, useAppSelector } from "features/store";
import { pushNotification } from "features/notificationSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BackendUser, Client, PlatformUser } from "types/interfaces/adminUsers";
import {
  BackendUserAutocompleteListbox,
  backendUserAutocompleteComponentsProps,
} from "components/common/BackendUserLabelBlock";
import Layout from "../components/Layout";

const umTheme = createTheme(baseTheme, {
  typography: {
    fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "uppercase" as const,
          letterSpacing: "0.06em",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          textTransform: "uppercase" as const,
          fontSize: 11,
          letterSpacing: "0.07em",
          fontWeight: 700,
          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
        },
      },
    },
  },
});

const DEFAULT_PAGE_SIZE = 50;
const TAB_STORAGE_KEY = "userManagementTab";
const BACKEND_SELECTED_KEY = "userManagementSelectedBackendId";

type ActiveTab =
  | "clients"
  | "client_users"
  | "internal_users"
  | "backend_users";

export default function UserManagementPage() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);
  const pp = currentUser?.platform_profile;
  const canCreateUsers = Boolean(pp?.is_superuser);
  const isSuperUser = Boolean(pp?.is_superuser);
  const isRegularInternalUser = Boolean(
    pp?.user_type === "internal" &&
    !pp?.is_superuser &&
    !pp?.is_calibrate_manager,
  );

  const theme = useTheme();
  const isWide = useMediaQuery("(min-width:1200px)");
  const isMedium = useMediaQuery("(min-width:768px)");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const showToast = (message: string, severity: "success" | "error") =>
    dispatch(pushNotification({ message, severity }));

  // ── Active tab ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    try {
      const s = window.localStorage.getItem(
        TAB_STORAGE_KEY,
      ) as ActiveTab | null;
      const allowedTabs: ActiveTab[] = isRegularInternalUser
        ? ["clients", "client_users"]
        : ["clients", "client_users", "internal_users", "backend_users"];
      if (s && allowedTabs.includes(s)) return s;
    } catch {}
    return "clients";
  });

  const handleTabChange = (_: React.SyntheticEvent, value: ActiveTab) => {
    setActiveTab(value);
    setPage(1);
    setSearch("");
    setRoleFilter(undefined);
    setBackendLabelFilter(undefined);
    setClientFilter(undefined);
    try {
      window.localStorage.setItem(TAB_STORAGE_KEY, value);
    } catch {}
  };

  // ── Shared state ────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<
    GetAdminUsersParams["sort_by"] | undefined
  >(undefined);
  const [sortOrder, setSortOrder] =
    useState<GetAdminUsersParams["sort_order"]>("asc");
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
  const [backendLabelFilter, setBackendLabelFilter] = useState<
    "migration" | "integration" | "sftp" | undefined
  >(undefined);
  const [clientFilter, setClientFilter] = useState<number | undefined>(
    undefined,
  );

  // ── Clients specific state ───────────────────────────────────────────────────
  const [clientSortBy, setClientSortBy] = useState<
    "company_name" | "created_at" | "owner" | "status" | "users" | undefined
  >(undefined);
  const [clientSortOrder, setClientSortOrder] = useState<"asc" | "desc">("asc");
  const [ownerFilter, setOwnerFilter] = useState<number | undefined>(undefined);

  const handleSortChange = (field: GetAdminUsersParams["sort_by"]) => {
    if (sortBy === field) {
      if (sortOrder === "asc") setSortOrder("desc");
      else {
        setSortBy(undefined);
        setSortOrder("asc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const handleClientSortChange = (
    field: "company_name" | "created_at" | "owner" | "status" | "users",
  ) => {
    if (clientSortBy === field) {
      if (clientSortOrder === "asc") setClientSortOrder("desc");
      else {
        setClientSortBy(undefined);
        setClientSortOrder("asc");
      }
    } else {
      setClientSortBy(field);
      setClientSortOrder("asc");
    }
    setPage(1);
  };

  // ── Clients tab data ────────────────────────────────────────────────────────
  const {
    data: clientsData,
    isLoading: isClientsLoading,
    isFetching: isClientsFetching,
  } = useGetClientsQuery(
    {
      page,
      search: search || undefined,
      page_size: pageSize,
      sort_by: clientSortBy,
      sort_order: clientSortOrder,
      owner_id: ownerFilter,
    },
    {
      skip: activeTab !== "clients",
      refetchOnMountOrArgChange: true,
    },
  );
  const clients = clientsData?.results ?? [];
  const clientsTotal = clientsData?.count ?? 0;
  const clientsTotalPages = Math.ceil(clientsTotal / pageSize) || 1;

  const [clientFilterSearch, setClientFilterSearch] = useState("");
  const [selectedFilterClient, setSelectedFilterClient] =
    useState<Client | null>(null);
  const {
    options: filterClientOptions,
    onListboxScroll: filterClientsOnScroll,
  } = useClientsAutocompleteInfinite({
    skip: activeTab !== "client_users",
    search: clientFilterSearch,
    value: selectedFilterClient,
  });
  const ALL_CLIENTS_OPTION = { id: null as number | null, label: "All" };
  const clientFilterAcOptions = [
    ALL_CLIENTS_OPTION,
    ...filterClientOptions.map((c) => ({
      id: c.id as number | null,
      label: `${c.company_name} (${c.company_code})`,
    })),
  ];
  const clientFilterAcValue =
    clientFilter == null
      ? ALL_CLIENTS_OPTION
      : {
          id: clientFilter as number | null,
          label: selectedFilterClient
            ? `${selectedFilterClient.company_name} (${selectedFilterClient.company_code})`
            : "",
        };
  const handleClientFilterChange = (
    nv: { id: number | null; label: string } | null,
  ) => {
    setClientFilter(nv?.id ?? undefined);
    setSelectedFilterClient(
      nv?.id != null
        ? (filterClientOptions.find((c) => c.id === nv.id) ?? null)
        : null,
    );
    setPage(1);
  };
  const handleClientFilterInputChange = (
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === "input") setClientFilterSearch(value);
    else if (reason === "clear") setClientFilterSearch("");
  };

  // ── Internal users for owner filter ─────────────────────────────────────────
  const { data: internalUsersData } = useGetAdminUsersQuery(
    {
      page: 1,
      page_size: 1000,
      user_type: "internal",
    },
    { skip: false },
  );
  const internalUsers = internalUsersData?.results ?? [];
  const isUserTab =
    activeTab === "client_users" || activeTab === "internal_users";
  const userTypeFilter =
    activeTab === "client_users"
      ? ("client" as const)
      : activeTab === "internal_users"
        ? ("internal" as const)
        : undefined;

  const { data, isLoading, isFetching } = useGetAdminUsersQuery(
    {
      page,
      search: search || undefined,
      page_size: pageSize,
      role: roleFilter,
      user_type: userTypeFilter,
      ...(activeTab === "client_users" && clientFilter
        ? { client_id: clientFilter }
        : {}),
      ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
    },
    { skip: !isUserTab, refetchOnMountOrArgChange: true },
  );
  const users = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  // ── Backend users tab data ──────────────────────────────────────────────────
  const {
    data: backendData,
    isLoading: isBackendLoading,
    isFetching: isBackendFetching,
  } = useGetBackendUsersQuery(
    {
      page,
      search: search || undefined,
      page_size: pageSize,
      ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
    },
    { skip: activeTab !== "backend_users" },
  );
  const backendUsersRaw = backendData?.results ?? [];
  const backendUsers =
    backendLabelFilter === "migration"
      ? backendUsersRaw.filter((u) => u.folder_count > 0)
      : backendLabelFilter === "integration"
        ? backendUsersRaw.filter((u) => u.integration_count > 0)
        : backendLabelFilter === "sftp"
          ? backendUsersRaw.filter((u) => u.sftp_count > 0)
          : backendUsersRaw;
  const backendTotalItems = backendData?.count ?? 0;
  const backendTotalPages = Math.ceil(backendTotalItems / pageSize) || 1;

  // ── Client modal ────────────────────────────────────────────────────────────
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const openClientCreate = () => {
    if (clientModalOpen && clientModalUnsaved) {
      pendingClientLeaveActionRef.current = () => {
        setEditingClient(null);
        setClientModalUnsaved(false);
      };
      setClientLeaveDialogOpen(true);
      return;
    }
    setEditingClient(null);
    setClientModalOpen(true);
  };
  const openClientEdit = (c: Client) => {
    if (clientModalOpen && clientModalUnsaved) {
      pendingClientLeaveActionRef.current = () => {
        setEditingClient(c);
        setClientModalUnsaved(false);
      };
      setClientLeaveDialogOpen(true);
      return;
    }
    setEditingClient(c);
    setClientModalOpen(true);
  };

  // ── Platform user (edit) modal ──────────────────────────────────────────────
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [panelResetKey, setPanelResetKey] = useState(0);
  const [platformPanelUnsaved, setPlatformPanelUnsaved] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const pendingLeaveDraftKeyRef = useRef<number | null>(null);
  const pendingLeaveActionRef = useRef<(() => void) | null>(null);

  const [clientModalUnsaved, setClientModalUnsaved] = useState(false);
  const [clientLeaveDialogOpen, setClientLeaveDialogOpen] = useState(false);
  const [clientLeaveSummary, setClientLeaveSummary] = useState<string[]>([]);
  const pendingClientLeaveActionRef = useRef<(() => void) | null>(null);
  const createdUserRef = useRef<PlatformUser | null>(null);
  const createdAtRef = useRef<number | null>(null);

  const [drafts, setDrafts] = useState<Record<number, UserManagementDrafts>>(
    loadPlatformUserDrafts,
  );
  const [staleDraftsDialogOpen, setStaleDraftsDialogOpen] = useState(() =>
    hasAnyPlatformUserDrafts(loadPlatformUserDrafts()),
  );

  const panelUser: PlatformUser | null = createMode
    ? null
    : selectedUserId == null
      ? null
      : (users.find((u) => u.id === selectedUserId) ??
        createdUserRef.current ??
        null);

  const onPlatformUnsavedChange = useCallback(
    (dirty: boolean) => setPlatformPanelUnsaved(dirty),
    [],
  );

  const closePlatformModal = useCallback(() => {
    setUserModalOpen(false);
    setCreateMode(false);
    setSelectedUserId(null);
    createdUserRef.current = null;
    createdAtRef.current = null;
    clearPersistedPlatformUserSelection();
  }, []);

  const requestClosePlatformModal = useCallback(() => {
    if (!platformPanelUnsaved) {
      closePlatformModal();
      return;
    }
    pendingLeaveDraftKeyRef.current = createMode
      ? CREATE_MODE_DRAFT_KEY
      : selectedUserId!;
    pendingLeaveActionRef.current = closePlatformModal;
    setLeaveDialogOpen(true);
  }, [platformPanelUnsaved, createMode, selectedUserId, closePlatformModal]);

  const onClientUnsavedChange = useCallback(
    (dirty: boolean) => setClientModalUnsaved(dirty),
    [],
  );

  const onClientUnsavedSummaryChange = useCallback(
    (summary: string[]) => setClientLeaveSummary(summary),
    [],
  );

  const requestCloseClientModal = useCallback(() => {
    if (!clientModalUnsaved) {
      setClientModalOpen(false);
      return;
    }
    pendingClientLeaveActionRef.current = () => setClientModalOpen(false);
    setClientLeaveDialogOpen(true);
  }, [clientModalUnsaved]);

  const handlePlatformDraftsChange = useCallback(
    (
      updater: (
        prev: Record<number, UserManagementDrafts>,
      ) => Record<number, UserManagementDrafts>,
    ) => {
      setDrafts((prev) => {
        const nextRaw = updater(prev);
        const ids = Object.keys(nextRaw)
          .map((k) => parseInt(k, 10))
          .filter((id) => !Number.isNaN(id))
          .sort((a, b) => a - b);
        const preferredKey =
          ids.find((id) => id === (selectedUserId ?? CREATE_MODE_DRAFT_KEY)) ??
          ids[ids.length - 1];
        const next =
          preferredKey == null ? {} : { [preferredKey]: nextRaw[preferredKey] };
        savePlatformUserDrafts(next);
        return next;
      });
    },
    [selectedUserId],
  );

  const handleDiscardAndLeavePlatform = useCallback(() => {
    const key = pendingLeaveDraftKeyRef.current;
    const fn = pendingLeaveActionRef.current;
    pendingLeaveDraftKeyRef.current = null;
    pendingLeaveActionRef.current = null;
    setLeaveDialogOpen(false);
    if (key != null)
      handlePlatformDraftsChange((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    setPanelResetKey((k) => k + 1);
    if (fn) {
      fn();
      return;
    }
    closePlatformModal();
  }, [handlePlatformDraftsChange, closePlatformModal]);

  const openUserEdit = useCallback((user: PlatformUser) => {
    setCreateMode(false);
    setSelectedUserId(user.id);
    setUserModalOpen(true);
    try {
      window.localStorage.setItem(
        PLATFORM_SELECTED_USER_STORAGE_KEY,
        String(user.id),
      );
    } catch {}
  }, []);

  const openUserCreate = useCallback(() => {
    createdUserRef.current = null;
    createdAtRef.current = null;
    clearPersistedPlatformUserSelection();
    setCreateMode(true);
    setSelectedUserId(null);
    setUserModalOpen(true);
  }, []);

  useEffect(() => {
    if (!platformPanelUnsaved || !userModalOpen) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [platformPanelUnsaved, userModalOpen]);

  useEffect(() => {
    if (!clientModalUnsaved || !clientModalOpen) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [clientModalUnsaved, clientModalOpen]);

  const draftClientNameByUserId = useMemo(() => {
    const m: Partial<Record<number, string>> = {};
    for (const u of users) {
      const name = (u.first_name || u.username || "").trim();
      m[u.id] = name || `User ID ${u.id}`;
    }
    if (panelUser) {
      const name = (panelUser.first_name || panelUser.username || "").trim();
      m[panelUser.id] = name || `User ID ${panelUser.id}`;
    }
    return m;
  }, [users, panelUser]);

  const staleDraftSummaryRows = useMemo(
    () =>
      staleDraftsDialogOpen
        ? summarizeDraftsForDialog(drafts, draftClientNameByUserId)
        : [],
    [drafts, draftClientNameByUserId, staleDraftsDialogOpen],
  );

  const leaveDraftSummaryRows = useMemo(() => {
    if (!leaveDialogOpen) return [];
    const pendingKey = pendingLeaveDraftKeyRef.current;
    if (pendingKey == null) return [];
    return summarizeDraftsForDialog(drafts, draftClientNameByUserId).filter(
      (row) => row.userId === pendingKey,
    );
  }, [drafts, draftClientNameByUserId, leaveDialogOpen]);

  const clientLeaveRows = useMemo(() => {
    if (!clientLeaveDialogOpen || !editingClient) return [];
    return [
      {
        userId: editingClient.id,
        userLabel: editingClient.company_name,
        sections: clientLeaveSummary,
      },
    ];
  }, [clientLeaveDialogOpen, editingClient, clientLeaveSummary]);

  useEffect(() => {
    if (!isUserTab) return;
    const updates: Record<number, string> = {};
    for (const u of users) {
      const name = (u.first_name || u.username || "").trim();
      updates[u.id] = name || `User ID ${u.id}`;
    }
    if (panelUser) {
      const name = (panelUser.first_name || panelUser.username || "").trim();
      updates[panelUser.id] = name || `User ID ${panelUser.id}`;
    }
    if (Object.keys(updates).length > 0)
      mergeDraftClientNamesToSession(updates);
  }, [isUserTab, users, panelUser]);

  // ── Backend user modal ──────────────────────────────────────────────────────
  const [backendModalOpen, setBackendModalOpen] = useState(false);
  const [selectedBackendId, setSelectedBackendId] = useState<number | null>(
    null,
  );
  const backendPanelUser =
    selectedBackendId == null
      ? null
      : (backendUsers.find((u) => u.id === selectedBackendId) ?? null);

  const openBackendCreate = () => {
    setSelectedBackendId(null);
    setBackendModalOpen(true);
  };

  const openBackendEdit = (user: BackendUser) => {
    setSelectedBackendId(user.id);
    setBackendModalOpen(true);
    try {
      window.localStorage.setItem(BACKEND_SELECTED_KEY, String(user.id));
    } catch {}
  };
  const closeBackendModal = () => {
    setBackendModalOpen(false);
    setSelectedBackendId(null);
    try {
      window.localStorage.removeItem(BACKEND_SELECTED_KEY);
    } catch {}
  };

  // ── Pagination helper ───────────────────────────────────────────────────────
  const paginationNode = (
    totalCount: number,
    totalPagesCount: number,
    fetching: boolean,
  ) =>
    totalCount > 0 ? (
      <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
        <PaginationControls
          currentLimit={pageSize}
          onLimitChange={(limit) => {
            setPageSize(limit);
            setPage(1);
          }}
          totalItems={totalCount}
          currentPage={page}
          totalPages={totalPagesCount}
          onPageChange={setPage}
          showLimitSelector
          showPageInfo
          disabled={fetching}
          size="small"
          pageTokens={[]}
          hasNextPage={page < totalPagesCount}
          borderStyle={{ borderTop: "none" }}
        />
      </Box>
    ) : null;

  const isClientTab = activeTab === "clients";
  const isBackendTab = activeTab === "backend_users";

  const searchPlaceholder = isClientTab
    ? "Search clients…"
    : isBackendTab
      ? "Search backend users…"
      : "Search users…";

  const roleFilterChips =
    activeTab === "internal_users"
      ? ([
          { value: "ai_agent", label: "AI Agent" },
          { value: "superuser", label: "Super Admin" },
          { value: "calibrate_manager", label: "Manager" },
          { value: "partnership_team", label: "Partnership Manager" },
          { value: "archived_pending", label: "Archived pending" },
          { value: "archived", label: "Archived" },
        ] as const)
      : activeTab === "client_users"
        ? ([
            { value: "data_storage", label: "Data Storage" },
            { value: "integration_dashboard", label: "Integration" },
            { value: "ai_agent", label: "AI Agent" },
            { value: "secure_file_upload", label: "Secure File Upload" },
            { value: "archived_pending", label: "Archived pending" },
            { value: "archived", label: "Archived" },
          ] as const)
        : null;

  const backendFilterChips =
    activeTab === "backend_users"
      ? ([
          { key: "migration" as const, label: "Migration" },
          { key: "integration" as const, label: "Integration" },
          { key: "sftp" as const, label: "SFTP" },
        ] as const)
      : null;

  const chromePad = `${SHELL_MAIN_CHROME_PADDING_PX}px`;

  return (
    <Layout title="User Management" content="User Management" noScroll>
      <ThemeProvider theme={umTheme}>
        {/* Grey chrome area */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            bgcolor: CALIBRATE_COLORS.chromeBackground,
            p: chromePad,
            boxSizing: "border-box",
            minHeight: 0,
          }}
        >
          {/* Page title in chrome */}
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 700,
              mb: 1.5,
              color: "text.primary",
              fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
              flexShrink: 0,
            }}
          >
            User Management
          </Typography>

          {/* White panel */}
          <Box
            sx={(theme) => ({
              bgcolor: "background.paper",
              borderRadius: `${CALIBRATE_RADIUS_MD}px`,
              boxShadow: theme.shadows[2],
              width: "100%",
              flex: 1,
              minHeight: 0,
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              overflow: "hidden",
            })}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                px: { xs: 2, sm: 3 },
                pt: 2,
                pb: 2,
                boxSizing: "border-box",
              }}
            >
              {/* ── Tab bar ─────────────────────────────────────────────────────── */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  mb: 1.5,
                  gap: 2,
                  flexShrink: 0,
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "uppercase",
                      fontWeight: 700,
                      minHeight: 44,
                      fontSize: 12,
                      letterSpacing: "0.06em",
                    },
                  }}
                >
                  <Tab label="Clients" value="clients" />
                  <Tab label="Client Users" value="client_users" />
                  {!isRegularInternalUser && (
                    <Tab label="Internal Users" value="internal_users" />
                  )}
                  {isSuperUser && (
                    <Tab label="Backend Users" value="backend_users" />
                  )}
                </Tabs>
              </Box>

              {/* ── Search + filter select ─────────────────────────────────── */}
              {isWide ? (
                <Box
                  mb={1.5}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ flexShrink: 0 }}
                >
                  <Box sx={{ width: 360, flexShrink: 0 }}>
                    <FileSearch
                      onSearch={(term) => {
                        setSearch(term || "");
                        setPage(1);
                      }}
                      placeholder={searchPlaceholder}
                      autoComplete="off"
                    />
                  </Box>
                  {isClientTab && (
                    <Autocomplete
                      size="small"
                      sx={{
                        minWidth: 200,
                        flexShrink: 0,
                        "& .MuiOutlinedInput-root": { height: "40px" },
                      }}
                      options={[
                        { id: null as number | null, label: "All Owners" },
                        ...internalUsers.map((u) => ({
                          id: u.id,
                          label:
                            `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                            u.username,
                        })),
                      ]}
                      value={
                        ownerFilter == null
                          ? { id: null, label: "All Owners" }
                          : {
                              id: ownerFilter,
                              label:
                                internalUsers.find((u) => u.id === ownerFilter)
                                  ?.first_name ||
                                internalUsers.find((u) => u.id === ownerFilter)
                                  ?.username ||
                                "",
                            }
                      }
                      onChange={(_, newValue) => {
                        setOwnerFilter(newValue?.id ?? undefined);
                        setPage(1);
                      }}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      ListboxComponent={BackendUserAutocompleteListbox}
                      componentsProps={backendUserAutocompleteComponentsProps}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Owner"
                          size="small"
                          placeholder="Search owners…"
                        />
                      )}
                    />
                  )}
                  {(roleFilterChips || backendFilterChips) && (
                    <Autocomplete
                      size="small"
                      sx={{
                        minWidth: 180,
                        flexShrink: 0,
                        "& .MuiOutlinedInput-root": { height: "40px" },
                      }}
                      options={[
                        { id: "", label: "All" },
                        ...(roleFilterChips ?? []).map(({ value, label }) => ({
                          id: value,
                          label,
                        })),
                        ...(backendFilterChips ?? []).map(({ key, label }) => ({
                          id: key,
                          label,
                        })),
                      ]}
                      value={(() => {
                        if (roleFilter && roleFilterChips) {
                          const chip = [...roleFilterChips].find(
                            (c) => c.value === roleFilter,
                          );
                          return { id: roleFilter, label: chip?.label ?? "" };
                        }
                        if (backendLabelFilter && backendFilterChips) {
                          const chip = [...backendFilterChips].find(
                            (c) => c.key === backendLabelFilter,
                          );
                          return {
                            id: backendLabelFilter,
                            label: chip?.label ?? "",
                          };
                        }
                        return { id: "", label: "All" };
                      })()}
                      onChange={(_, newValue) => {
                        const val = newValue?.id ?? "";
                        if (roleFilterChips) {
                          setRoleFilter(val === "" ? undefined : val);
                        } else {
                          setBackendLabelFilter(
                            val === ""
                              ? undefined
                              : (val as "migration" | "integration" | "sftp"),
                          );
                        }
                        setPage(1);
                      }}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      ListboxComponent={BackendUserAutocompleteListbox}
                      componentsProps={backendUserAutocompleteComponentsProps}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Filter"
                          size="small"
                          placeholder="Filter by…"
                        />
                      )}
                    />
                  )}
                  {activeTab === "client_users" && (
                    <Autocomplete
                      size="small"
                      sx={{
                        minWidth: 200,
                        flexShrink: 0,
                        "& .MuiOutlinedInput-root": { height: "40px" },
                      }}
                      options={clientFilterAcOptions}
                      value={clientFilterAcValue}
                      onChange={(_, newValue) =>
                        handleClientFilterChange(newValue)
                      }
                      onInputChange={(_, v, reason) =>
                        handleClientFilterInputChange(v, reason)
                      }
                      filterOptions={(x) => x}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      ListboxComponent={BackendUserAutocompleteListbox}
                      ListboxProps={{ onScroll: filterClientsOnScroll }}
                      componentsProps={backendUserAutocompleteComponentsProps}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client"
                          size="small"
                          placeholder="Search clients…"
                        />
                      )}
                    />
                  )}
                  <Box sx={{ ml: "auto", flexShrink: 0 }}>
                    {isClientTab && (
                      <Tooltip
                        title={!canCreateUsers ? "Super User required." : ""}
                      >
                        <span>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={openClientCreate}
                            disabled={!canCreateUsers}
                            sx={{ fontWeight: 700, minHeight: 36 }}
                          >
                            + New Client
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {isUserTab && (
                      <Tooltip
                        title={!canCreateUsers ? "Super User required." : ""}
                      >
                        <span>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={openUserCreate}
                            disabled={!canCreateUsers}
                            sx={{ fontWeight: 700, minHeight: 36 }}
                          >
                            + New User
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {isBackendTab && (
                      <Tooltip
                        title={!canCreateUsers ? "Super User required." : ""}
                      >
                        <span>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={openBackendCreate}
                            disabled={!canCreateUsers}
                            sx={{ fontWeight: 700, minHeight: 36 }}
                          >
                            + New Backend User
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box
                  mb={1.5}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ flexShrink: 0 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FileSearch
                      onSearch={(term) => {
                        setSearch(term || "");
                        setPage(1);
                      }}
                      placeholder={searchPlaceholder}
                      autoComplete="off"
                    />
                  </Box>
                  <IconButton
                    color="primary"
                    aria-label="Open filters and actions"
                    onClick={() => setFilterDrawerOpen(true)}
                    sx={{
                      flexShrink: 0,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Tune />
                  </IconButton>
                </Box>
              )}

              {/* ── Table area ───────────────────────────────────────────────────── */}
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flex: 1,
                  minHeight: 0,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
                {isClientTab && (
                  <ClientsTable
                    clients={clients}
                    isLoading={isClientsLoading}
                    isFetching={isClientsFetching}
                    onEdit={openClientEdit}
                    sortBy={clientSortBy}
                    sortOrder={clientSortOrder}
                    onSortChange={handleClientSortChange}
                    pagination={paginationNode(
                      clientsTotal,
                      clientsTotalPages,
                      isClientsFetching,
                    )}
                  />
                )}
                {isUserTab && (
                  <UserManagementTable
                    users={users}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    selectedUserId={selectedUserId ?? undefined}
                    onManage={openUserEdit}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={handleSortChange}
                    userType={activeTab as "client_users" | "internal_users"}
                    hasUnsavedChangesForUser={(userId) => {
                      const d = drafts[userId];
                      if (!d) return false;
                      return Boolean(
                        d.folders ||
                        d.integrations ||
                        d.chatbot ||
                        d.permissions ||
                        d.profile,
                      );
                    }}
                    pagination={paginationNode(
                      totalItems,
                      totalPages,
                      isFetching,
                    )}
                  />
                )}
                {isBackendTab && (
                  <BackendUserTable
                    users={backendUsers}
                    isLoading={isBackendLoading}
                    isFetching={isBackendFetching}
                    selectedUserId={selectedBackendId ?? undefined}
                    onManage={openBackendEdit}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={handleSortChange}
                    pagination={paginationNode(
                      backendTotalItems,
                      backendTotalPages,
                      isBackendFetching,
                    )}
                  />
                )}
              </Box>

              {/* ── Modals ──────────────────────────────────────────────────────── */}
              <ClientEditModal
                open={clientModalOpen}
                client={editingClient}
                onClose={requestCloseClientModal}
                onSaved={(saved) => {
                  setEditingClient(saved);
                  setClientModalOpen(false);
                  setClientModalUnsaved(false);
                  setClientLeaveSummary([]);
                }}
                onUnsavedChange={onClientUnsavedChange}
                onUnsavedSummaryChange={onClientUnsavedSummaryChange}
              />

              <UserEditModal
                key={`${createMode ? "n" : (selectedUserId ?? "x")}-${panelResetKey}`}
                open={userModalOpen}
                user={panelUser}
                onClose={closePlatformModal}
                onCloseRequest={requestClosePlatformModal}
                onUserUpdated={(updated) => {
                  createdUserRef.current = updated;
                  createdAtRef.current = Date.now();
                  setSelectedUserId(updated.id);
                  setCreateMode(false);
                  try {
                    window.localStorage.setItem(
                      PLATFORM_SELECTED_USER_STORAGE_KEY,
                      String(updated.id),
                    );
                  } catch {}
                }}
                onUserArchived={() =>
                  showToast("User archive status updated.", "success")
                }
                onSavingChange={(_saving) => {}}
                onUnsavedChange={onPlatformUnsavedChange}
                drafts={drafts}
                onDraftsChange={handlePlatformDraftsChange}
                userType={activeTab as "client_users" | "internal_users"}
              />

              <BackendUserEditModal
                open={backendModalOpen}
                user={backendPanelUser}
                onClose={closeBackendModal}
                onUserUpdated={(updated) => {
                  setSelectedBackendId(updated.id);
                  try {
                    window.localStorage.setItem(
                      BACKEND_SELECTED_KEY,
                      String(updated.id),
                    );
                  } catch {}
                }}
                onUserDeleted={() => {
                  showToast(
                    "User deleted. S3 cleanup has been queued.",
                    "success",
                  );
                  closeBackendModal();
                }}
              />

              {/* ── Unsaved draft dialogs ────────────────────────────────────────── */}
              <UnsavedPlatformDraftsDialog
                open={staleDraftsDialogOpen}
                onClose={() => setStaleDraftsDialogOpen(false)}
                title="Unsaved changes from last time"
                intro="You have unsaved changes. Discard them to continue or stay to keep editing."
                rows={staleDraftSummaryRows}
                onDiscardAndContinue={() => {
                  clearPlatformUserDrafts();
                  setDrafts({});
                  setStaleDraftsDialogOpen(false);
                  closePlatformModal();
                  setPanelResetKey((k) => k + 1);
                }}
              />

              <UnsavedPlatformDraftsDialog
                open={leaveDialogOpen}
                onClose={() => {
                  setLeaveDialogOpen(false);
                  pendingLeaveDraftKeyRef.current = null;
                  pendingLeaveActionRef.current = null;
                }}
                title="Unsaved changes"
                intro="You have unsaved changes. Discard them to continue or stay to keep editing."
                rows={leaveDraftSummaryRows}
                discardButtonLabel="Discard and continue"
                onDiscardAndContinue={handleDiscardAndLeavePlatform}
              />

              <UnsavedPlatformDraftsDialog
                open={clientLeaveDialogOpen}
                onClose={() => {
                  setClientLeaveDialogOpen(false);
                  pendingClientLeaveActionRef.current = null;
                }}
                title="Unsaved changes"
                intro="You have unsaved changes. Discard them to continue or stay to keep editing."
                rows={clientLeaveRows}
                discardButtonLabel="Discard and continue"
                onDiscardAndContinue={() => {
                  const fn = pendingClientLeaveActionRef.current;
                  pendingClientLeaveActionRef.current = null;
                  setClientLeaveDialogOpen(false);
                  setClientModalUnsaved(false);
                  fn?.();
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* ── Mobile / Tablet filter drawer ─────────────────────────────── */}
        <Drawer
          anchor={!isWide && isMedium ? "right" : "bottom"}
          open={!isWide && filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{
            sx:
              !isWide && isMedium
                ? { width: 360, p: 2 }
                : {
                    borderTopLeftRadius: 2,
                    borderTopRightRadius: 2,
                    maxHeight: "80vh",
                    p: 2,
                  },
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6" fontWeight={700}>
              Filters
            </Typography>
            <IconButton
              size="small"
              onClick={() => setFilterDrawerOpen(false)}
              sx={{ borderRadius: 1 }}
            >
              <Close />
            </IconButton>
          </Box>

          <Box display="flex" flexDirection="column" gap={2}>
            {isClientTab && (
              <Autocomplete
                size="small"
                fullWidth
                options={[
                  { id: null as number | null, label: "All Owners" },
                  ...internalUsers.map((u) => ({
                    id: u.id,
                    label:
                      `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                      u.username,
                  })),
                ]}
                value={
                  ownerFilter == null
                    ? { id: null, label: "All Owners" }
                    : {
                        id: ownerFilter,
                        label:
                          internalUsers.find((u) => u.id === ownerFilter)
                            ?.first_name ||
                          internalUsers.find((u) => u.id === ownerFilter)
                            ?.username ||
                          "",
                      }
                }
                onChange={(_, newValue) => {
                  setOwnerFilter(newValue?.id ?? undefined);
                  setPage(1);
                }}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                ListboxComponent={BackendUserAutocompleteListbox}
                componentsProps={backendUserAutocompleteComponentsProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Owner"
                    size="small"
                    placeholder="Search owners…"
                  />
                )}
              />
            )}
            {(roleFilterChips || backendFilterChips) && (
              <Autocomplete
                size="small"
                fullWidth
                options={[
                  { id: "", label: "All" },
                  ...(roleFilterChips ?? []).map(({ value, label }) => ({
                    id: value,
                    label,
                  })),
                  ...(backendFilterChips ?? []).map(({ key, label }) => ({
                    id: key,
                    label,
                  })),
                ]}
                value={(() => {
                  if (roleFilter && roleFilterChips) {
                    const chip = [...roleFilterChips].find(
                      (c) => c.value === roleFilter,
                    );
                    return { id: roleFilter, label: chip?.label ?? "" };
                  }
                  if (backendLabelFilter && backendFilterChips) {
                    const chip = [...backendFilterChips].find(
                      (c) => c.key === backendLabelFilter,
                    );
                    return {
                      id: backendLabelFilter,
                      label: chip?.label ?? "",
                    };
                  }
                  return { id: "", label: "All" };
                })()}
                onChange={(_, newValue) => {
                  const val = newValue?.id ?? "";
                  if (roleFilterChips) {
                    setRoleFilter(val === "" ? undefined : val);
                  } else {
                    setBackendLabelFilter(
                      val === ""
                        ? undefined
                        : (val as "migration" | "integration" | "sftp"),
                    );
                  }
                  setPage(1);
                }}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                ListboxComponent={BackendUserAutocompleteListbox}
                componentsProps={backendUserAutocompleteComponentsProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter"
                    size="small"
                    placeholder="Filter by…"
                  />
                )}
              />
            )}
            {activeTab === "client_users" && (
              <Autocomplete
                size="small"
                fullWidth
                options={clientFilterAcOptions}
                value={clientFilterAcValue}
                onChange={(_, newValue) => handleClientFilterChange(newValue)}
                onInputChange={(_, v, reason) =>
                  handleClientFilterInputChange(v, reason)
                }
                filterOptions={(x) => x}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                ListboxComponent={BackendUserAutocompleteListbox}
                ListboxProps={{ onScroll: filterClientsOnScroll }}
                componentsProps={backendUserAutocompleteComponentsProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    size="small"
                    placeholder="Search clients…"
                  />
                )}
              />
            )}
          </Box>

          <Box mt={3} display="flex" flexDirection="column" gap={1.5}>
            {isClientTab && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setFilterDrawerOpen(false);
                  openClientCreate();
                }}
                disabled={!canCreateUsers}
                sx={{ fontWeight: 700, minHeight: 40 }}
              >
                + New Client
              </Button>
            )}
            {isUserTab && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setFilterDrawerOpen(false);
                  openUserCreate();
                }}
                disabled={!canCreateUsers}
                sx={{ fontWeight: 700, minHeight: 40 }}
              >
                + New User
              </Button>
            )}
            {isBackendTab && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setFilterDrawerOpen(false);
                  openBackendCreate();
                }}
                disabled={!canCreateUsers}
                sx={{ fontWeight: 700, minHeight: 40 }}
              >
                + New Backend User
              </Button>
            )}
          </Box>
        </Drawer>
      </ThemeProvider>
    </Layout>
  );
}
